import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { z } from "zod";
import { tagsSchemas } from "@pineseed/spec";
import type { TagAutocompleteState, Tag, Workspace } from "@pineseed/gui/types";
import { DEFAULT_WORKSPACE_ID } from "../config/workspace";
import { userAccountsSchemas } from "@pineseed/spec";

// Types - using generated schemas for server-side tags
type ServerTag = z.infer<typeof tagsSchemas.Tag>;
type TagListResponse = z.infer<typeof tagsSchemas.TagListResponse>;

// Extract remote workspace type from generated schema
type RemoteWorkspace = z.infer<typeof userAccountsSchemas.Workspace>;
type RemoteWorkspacesList = z.infer<typeof userAccountsSchemas.WorkspacesList>;

/**
 * Workspace state atoms
 */

// export const setActiveWorkspaceAtom = atom(null, (get, set, workspace: Workspace) => {
//   console.log("🔄 Setting active workspace:", workspace);
//   set(activeWorkspaceIdAtom, workspace.id);
// });

// All workspaces - with default workspace auto-creation
export const workspacesAtom = atomWithStorage<Workspace[]>(
  "ive-workspaces",
  [],
  {
    getItem: (key, initialValue) => {
      const stored = localStorage.getItem(key);
      if (!stored) return initialValue;

      try {
        const workspaces = JSON.parse(stored) as Workspace[];

        // Convert date strings back to Date objects
        const workspacesWithDates: Workspace[] = workspaces.map(
          (
            w:
              | Workspace
              | (Omit<Workspace, "createdAt" | "updatedAt"> & {
                  createdAt: string | Date;
                  updatedAt: string | Date;
                }),
          ) => ({
            ...w,
            createdAt:
              w.createdAt instanceof Date ? w.createdAt : new Date(w.createdAt),
            updatedAt:
              w.updatedAt instanceof Date ? w.updatedAt : new Date(w.updatedAt),
          }),
        );

        // Auto-create default workspace if it doesn't exist and auto-create is enabled
        // Note: Using direct import since this is in a sync context
        // The config will be loaded at runtime when needed
        const hasDefault = workspacesWithDates.some(
          (w) => w.id === DEFAULT_WORKSPACE_ID,
        );
        if (!hasDefault) {
          // Create a basic default workspace - full config will be applied later
          const defaultWorkspace: Workspace = {
            id: DEFAULT_WORKSPACE_ID,
            name: "Default Workspace",
            description: "Your default IVE workspace",
            type: "chat" as const,
            icon: "💬",
            color: "#3b82f6",
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          workspacesWithDates.unshift(defaultWorkspace);
          // console.log(
          //   `🏢 Auto-created default workspace: ${DEFAULT_WORKSPACE_ID}`,
          // );
        }

        return workspacesWithDates;
      } catch {
        return initialValue;
      }
    },
    setItem: (key, value) => {
      localStorage.setItem(key, JSON.stringify(value));
    },
    removeItem: (key) => {
      localStorage.removeItem(key);
    },
  },
);

// Currently active workspace - defaults to default workspace ID
export const activeWorkspaceIdAtom = atomWithStorage<string | null>(
  "ive-active-workspace",
  DEFAULT_WORKSPACE_ID,
);

// Derived atom for active workspace
export const activeWorkspaceAtom = atom((get) => {
  const workspaces = get(availableWorkspacesAtom);
  const activeId = get(activeWorkspaceIdAtom);
  return workspaces.find((w) => w.id === activeId) || null;
});

// Workspace creation state
export const isCreatingWorkspaceAtom = atom(false);

// Workspace sidebar visibility
export const workspaceSidebarVisibleAtom = atomWithStorage(
  "ive-workspace-sidebar",
  true,
);

// Recently used workspaces
export const recentWorkspacesAtom = atom((get) => {
  const workspaces = get(workspacesAtom);
  return workspaces
    .sort(
      (a, b) =>
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
    )
    .slice(0, 5);
});

// Workspace actions
export const createWorkspaceAtom = atom(
  null,
  async (
    get,
    set,
    workspace: Omit<Workspace, "id" | "createdAt" | "updatedAt">,
  ) => {
    const workspaces = get(workspacesAtom);
    const { workspaceConfig } = await import("../config/workspace");
    const { envConfig } = await import("../config/env");
    const workspaceConfigInstance = workspaceConfig(envConfig);
    const newWorkspace: Workspace = {
      ...workspace,
      id: workspaceConfigInstance.getDefaultWorkspaceId(),
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    set(workspacesAtom, [...workspaces, newWorkspace]);
    set(activeWorkspaceIdAtom, newWorkspace.id);
    return newWorkspace;
  },
);

export const updateWorkspaceAtom = atom(
  null,
  (get, set, id: string, updates: Partial<Workspace>) => {
    const workspaces = get(workspacesAtom);
    const updatedWorkspaces = workspaces.map((w) =>
      w.id === id ? { ...w, ...updates, updatedAt: new Date() } : w,
    );
    set(workspacesAtom, updatedWorkspaces);
  },
);

export const deleteWorkspaceAtom = atom(null, (get, set, id: string) => {
  const workspaces = get(workspacesAtom);
  const activeId = get(activeWorkspaceIdAtom);

  set(
    workspacesAtom,
    workspaces.filter((w) => w.id !== id),
  );

  if (activeId === id) {
    const remaining = workspaces.filter((w) => w.id !== id);
    set(activeWorkspaceIdAtom, remaining.length > 0 ? remaining[0].id : null);
  }
});

// Tag state atoms (in-memory only, no localStorage persistence)
export const tagsAtom = atom<Tag[]>([]);

// Active workspace tags (derived, memoized)
const _activeWorkspaceTagsCache = { workspaceId: '', tags: [] as Tag[], result: [] as Tag[] };
export const activeWorkspaceTagsAtom = atom((get) => {
  const tags = get(tagsAtom);
  const activeWorkspaceId = get(activeWorkspaceIdAtom);
  if (!activeWorkspaceId) return [];
  
  // Return cached result if inputs haven't changed
  if (_activeWorkspaceTagsCache.workspaceId === activeWorkspaceId && _activeWorkspaceTagsCache.tags === tags) {
    return _activeWorkspaceTagsCache.result;
  }
  
  const result = tags.filter((tag) => tag.workspaceId === activeWorkspaceId);
  _activeWorkspaceTagsCache.workspaceId = activeWorkspaceId;
  _activeWorkspaceTagsCache.tags = tags;
  _activeWorkspaceTagsCache.result = result;
  return result;
});

// Active workspace tag count (derived) - combines local and server tags
export const activeWorkspaceTagCountAtom = atom((get) => {
  const localTags = get(activeWorkspaceTagsAtom);
  const serverTags = get(serverTagsAtom);
  const activeWorkspaceId = get(activeWorkspaceIdAtom);

  if (!activeWorkspaceId) return 0;

  // Count local tags for the active workspace
  const localTagCount = localTags.length;

  // Count server tags (they're already filtered for the active workspace by the party integration)
  const serverTagCount = serverTags.length;

  // Total count (server tags are separate from local tags)
  return localTagCount + serverTagCount;
});

// All workspace tags (derived) - combines local and server tags for tooltip display
export const allActiveWorkspaceTagsAtom = atom((get) => {
  const localTags = get(activeWorkspaceTagsAtom);
  const serverTags = get(serverTagsAtom);
  const activeWorkspaceId = get(activeWorkspaceIdAtom);

  if (!activeWorkspaceId) return [];

  // Convert server tags to local Tag format for consistent display
  const convertedServerTags: Tag[] = serverTags.map((serverTag) => ({
    ...serverTag,
    createdAt: new Date(serverTag.createdAt),
    updatedAt: new Date(serverTag.updatedAt),
  }));

  // Combine local and server tags, removing duplicates by name and type
  const allTags = [...localTags, ...convertedServerTags];
  const uniqueTags = allTags.filter(
    (tag, index, array) =>
      array.findIndex(
        (t) =>
          t.name.toLowerCase() === tag.name.toLowerCase() &&
          t.type === tag.type,
      ) === index,
  );

  // Sort tags by type first, then by name
  return uniqueTags.sort((a, b) => {
    if (a.type !== b.type) {
      return a.type.localeCompare(b.type);
    }
    return a.name.localeCompare(b.name);
  });
});

// Tag autocomplete state (transient)
export const tagAutocompleteStateAtom = atom<TagAutocompleteState>({
  isOpen: false,
  query: "",
  results: [],
  selectedIndex: 0,
  position: { x: 0, y: 0 },
  placement: {
    horizontal: "right",
    vertical: "below",
  },
});

// Tag search query (transient)
export const tagSearchQueryAtom = atom("");

// Tag actions
export const createTagAtom = atom(
  null,
  (
    get,
    set,
    tagData: Omit<Tag, "id" | "createdAt" | "updatedAt" | "workspaceId">,
  ) => {
    const tags = get(tagsAtom);
    const activeWorkspaceId = get(activeWorkspaceIdAtom);

    if (!activeWorkspaceId) {
      throw new Error("No active workspace");
    }

    // Check for existing tag with same name, type, and workspace
    const existingTag = tags.find(
      (tag) =>
        tag.name.toLowerCase() === tagData.name.toLowerCase() &&
        tag.type === tagData.type &&
        tag.workspaceId === activeWorkspaceId,
    );

    if (existingTag) {
      // console.log(
      //   `Tag "${tagData.name}" already exists, updating instead of creating duplicate`,
      // );
      // If tag exists, update it with new data and return the updated tag
      const updatedTag = {
        ...existingTag,
        ...tagData,
        updatedAt: new Date(),
      };

      const updatedTags = tags.map((tag) =>
        tag.id === existingTag.id ? updatedTag : tag,
      );

      set(tagsAtom, updatedTags);
      return updatedTag;
    }

    // Create new tag if it doesn't exist
    const newTag: Tag = {
      ...tagData,
      id: crypto.randomUUID(),
      workspaceId: activeWorkspaceId,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    set(tagsAtom, [...tags, newTag]);
    return newTag;
  },
);

export const updateTagAtom = atom(
  null,
  (get, set, id: string, updates: Partial<Omit<Tag, "id" | "createdAt">>) => {
    const tags = get(tagsAtom);
    const updatedTags = tags.map((tag) =>
      tag.id === id ? { ...tag, ...updates, updatedAt: new Date() } : tag,
    );
    set(tagsAtom, updatedTags);
  },
);

export const deleteTagAtom = atom(null, (get, set, id: string) => {
  const tags = get(tagsAtom);
  set(
    tagsAtom,
    tags.filter((tag) => tag.id !== id),
  );
});

export const findOrCreateTagAtom = atom(
  null,
  (get, set, name: string, type: Tag["type"] = "general") => {
    const tags = get(activeWorkspaceTagsAtom);

    const existing = tags.find(
      (tag) =>
        tag.name.toLowerCase() === name.toLowerCase() && tag.type === type,
    );

    if (existing) {
      return existing;
    }

    return set(createTagAtom, { name, type, namespace: "general" });
  },
);

// Tag autocomplete actions
export const openTagAutocompleteAtom = atom(
  null,
  (
    _get,
    set,
    position: { x: number; y: number },
    query: string = "",
    placement: { horizontal: "left" | "right"; vertical: "above" | "below" } = {
      horizontal: "right",
      vertical: "below",
    },
  ) => {
    set(tagAutocompleteStateAtom, {
      isOpen: true,
      query,
      results: [],
      selectedIndex: 0,
      position,
      placement,
    });
  },
);

export const closeTagAutocompleteAtom = atom(null, (_get, set) => {
  set(tagAutocompleteStateAtom, {
    isOpen: false,
    query: "",
    results: [],
    selectedIndex: 0,
    position: { x: 0, y: 0 },
    placement: {
      horizontal: "right",
      vertical: "below",
    },
  });
});

export const updateTagAutocompleteQueryAtom = atom(
  null,
  (get, set, query: string) => {
    const currentState = get(tagAutocompleteStateAtom);
    set(tagAutocompleteStateAtom, {
      ...currentState,
      query,
      selectedIndex: 0, // Reset selection when query changes
    });
  },
);

export const setTagAutocompleteResultsAtom = atom(
  null,
  (get, set, results: TagAutocompleteState["results"]) => {
    const currentState = get(tagAutocompleteStateAtom);
    set(tagAutocompleteStateAtom, {
      ...currentState,
      results,
    });
  },
);

export const updateTagAutocompleteSelectionAtom = atom(
  null,
  (get, set, direction: "up" | "down") => {
    const currentState = get(tagAutocompleteStateAtom);
    const maxIndex = currentState.results.length - 1;

    let newIndex = currentState.selectedIndex;
    if (direction === "down") {
      newIndex = Math.min(maxIndex, currentState.selectedIndex + 1);
    } else {
      newIndex = Math.max(0, currentState.selectedIndex - 1);
    }

    set(tagAutocompleteStateAtom, {
      ...currentState,
      selectedIndex: newIndex,
    });
  },
);

// Tag registration system
export const registerTagAtom = atom(
  null,
  (
    get,
    set,
    tagData: Omit<Tag, "id" | "createdAt" | "updatedAt" | "workspaceId">,
  ) => {
    const tags = get(tagsAtom);
    const activeWorkspaceId = get(activeWorkspaceIdAtom);

    if (!activeWorkspaceId) {
      console.warn("Cannot register tag: No active workspace");
      return null;
    }

    // Check if tag already exists
    const existingTag = tags.find(
      (tag) =>
        tag.name.toLowerCase() === tagData.name.toLowerCase() &&
        tag.type === tagData.type &&
        tag.workspaceId === activeWorkspaceId,
    );

    if (existingTag) {
      // Update existing tag with new data
      const updatedTag = {
        ...existingTag,
        ...tagData,
        updatedAt: new Date(),
      };

      const updatedTags = tags.map((tag) =>
        tag.id === existingTag.id ? updatedTag : tag,
      );

      set(tagsAtom, updatedTags);
      return updatedTag;
    }

    // Create new tag
    const newTag: Tag = {
      ...tagData,
      id: crypto.randomUUID(),
      workspaceId: activeWorkspaceId,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    set(tagsAtom, [...tags, newTag]);
    return newTag;
  },
);

export const unregisterTagAtom = atom(
  null,
  (get, set, name: string, type: Tag["type"]) => {
    const tags = get(tagsAtom);
    const activeWorkspaceId = get(activeWorkspaceIdAtom);

    if (!activeWorkspaceId) {
      console.warn("Cannot unregister tag: No active workspace");
      return;
    }

    const updatedTags = tags.filter(
      (tag) =>
        !(
          tag.name.toLowerCase() === name.toLowerCase() &&
          tag.type === type &&
          tag.workspaceId === activeWorkspaceId
        ),
    );

    set(tagsAtom, updatedTags);
  },
);

export const unregisterTagByIdAtom = atom(null, (get, set, tagId: string) => {
  const tags = get(tagsAtom);
  set(
    tagsAtom,
    tags.filter((tag) => tag.id !== tagId),
  );
});

// Server tags from party system (separate from local tag system)
export const serverTagsAtom = atom<ServerTag[]>([]);

// Server tag response state (complete response from server)
export const serverTagResponseAtom = atom<TagListResponse | null>(null);

// Atom to fetch server tags via party integration (workspace-level only)
export const fetchServerTagsAtom = atom(
  null,
  (_get, _set, _workspaceId: string) => {
    // console.log(
    //   `🏷️ Server tags for workspace ${workspaceId} are automatically fetched via workspace-level party integration SSE`,
    // );

    // Server tags are automatically fetched by the workspace-level party integration
    // via SSE subscription. This atom exists for compatibility but doesn't need to do anything
    // since the party integration handles tag updates automatically through onTagsUpdate callback

    return true;
  },
);

// Atom to update server tags (called by party handler via callback pattern)
export const updateServerTagsAtom = atom(
  null,
  (_get, set, tags: ServerTag[]) => {
    // console.log(
    //   `🏷️ Updating server tags: ${tags.length} tags received from party handler`,
    // );
    set(serverTagsAtom, tags);
  },
);

// Atom to update complete server tag response (includes metadata)
export const updateServerTagResponseAtom = atom(
  null,
  (_get, set, response: TagListResponse) => {
    // console.log(
    //   `🏷️ Updating server tag response: ${response.tags.length} tags`,
    // );
    set(serverTagResponseAtom, response);
    // Also update the tags array for backward compatibility
    set(serverTagsAtom, response.tags);
  },
);

// Auto-fetch server tags when workspace changes (follows our architecture)
export const autoFetchServerTagsAtom = atom(null, (get, set) => {
  const activeWorkspaceId = get(activeWorkspaceIdAtom);

  if (activeWorkspaceId) {
    // console.log(
    //   `🏷️ Active workspace changed to: ${activeWorkspaceId}, triggering server tags fetch`,
    // );
    // Use the fetchServerTagsAtom which follows proper callback pattern
    set(fetchServerTagsAtom, activeWorkspaceId);
  } else {
    console.log("🏷️ No active workspace, clearing server tags");
    set(serverTagsAtom, []);
  }
});

export const cleanupDuplicateTagsAtom = atom(null, (get, set) => {
  const tags = get(tagsAtom);
  const seen = new Map<string, Tag>();
  const uniqueTags: Tag[] = [];

  for (const tag of tags) {
    // Ensure dates are properly converted from strings if needed
    const normalizedTag = {
      ...tag,
      createdAt:
        tag.createdAt instanceof Date ? tag.createdAt : new Date(tag.createdAt),
      updatedAt:
        tag.updatedAt instanceof Date ? tag.updatedAt : new Date(tag.updatedAt),
    };

    const key = `${normalizedTag.workspaceId}-${normalizedTag.name.toLowerCase()}-${normalizedTag.type}`;
    const existing = seen.get(key);

    if (!existing) {
      // First occurrence - keep it
      seen.set(key, normalizedTag);
      uniqueTags.push(normalizedTag);
    } else {
      // Duplicate found - merge properties, keeping the most recent data
      const merged = {
        ...existing,
        ...normalizedTag,
        // Keep the original creation date but update the updated date
        createdAt: existing.createdAt,
        updatedAt: new Date(
          Math.max(
            existing.updatedAt.getTime(),
            normalizedTag.updatedAt.getTime(),
          ),
        ),
      };

      // Replace the existing tag in our tracking
      seen.set(key, merged);

      // Replace in the uniqueTags array
      const index = uniqueTags.findIndex((t) => t.id === existing.id);
      if (index >= 0) {
        uniqueTags[index] = merged;
      }
    }
  }

  if (uniqueTags.length !== tags.length) {
    console.log(`Cleaned up ${tags.length - uniqueTags.length} duplicate tags`);
    set(tagsAtom, uniqueTags);
  }
});

// Clear all searchable tags on load (call once before any tag registration)
export const clearSearchableTagsOnLoadAtom = atom(null, (get, set) => {
  const tags = get(tagsAtom);
  const activeWorkspaceId = get(activeWorkspaceIdAtom);

  if (!activeWorkspaceId) {
    console.warn("Cannot clear searchable tags: No active workspace");
    return;
  }

  // Filter out searchable tags for the current workspace
  const filteredTags = tags.filter((tag) => {
    if (tag.workspaceId !== activeWorkspaceId) {
      return true; // Keep tags from other workspaces
    }

    // Clear file and participant tags
    if (tag.type === "file" || tag.type === "participant") {
      return false;
    }

    // Clear participant group tags (general type with participant-group metadata)
    if (
      tag.type === "general" &&
      tag.metadata &&
      typeof tag.metadata === "object" &&
      "groupType" in tag.metadata &&
      tag.metadata.groupType === "participant-group"
    ) {
      return false;
    }

    return true; // Keep all other tags
  });

  const removedCount = tags.length - filteredTags.length;
  if (removedCount > 0) {
    console.log(
      `Cleared ${removedCount} searchable tags on load for workspace ${activeWorkspaceId}`,
    );
    set(tagsAtom, filteredTags);
  }
});

// ===== REMOTE WORKSPACES (FROM oRPC API) =====

/**
 * Remote workspaces response from oRPC API
 * Stores the complete response including metadata
 */
export const remoteWorkspacesResponseAtom = atom<RemoteWorkspacesList | null>(
  null,
);

/**
 * Remote workspaces fetched from oRPC API
 * These are the user's available workspaces from the server
 */
export const remoteWorkspacesAtom = atom<RemoteWorkspace[]>([]);

/**
 * Loading state for remote workspaces
 */
export const remoteWorkspacesLoadingAtom = atom<boolean>(false);

/**
 * Error state for remote workspaces
 */
export const remoteWorkspacesErrorAtom = atom<string | null>(null);

/**
 * Remote workspaces total count (derived from response)
 */
export const remoteWorkspacesTotalAtom = atom<number>((get) => {
  const response = get(remoteWorkspacesResponseAtom);
  return response?.total || 0;
});

/**
 * Fetch remote workspaces action atom - triggers handler callback (architectural compliance)
 */
export const fetchRemoteWorkspacesAtom = atom(null, (get, set) => {
  // Get handler callbacks to trigger the fetch
  const callbacks = get(workspaceHandlerCallbacksAtom);

  if (callbacks.onFetchRemoteWorkspaces) {
    // Set loading state immediately
    set(remoteWorkspacesLoadingAtom, true);
    set(remoteWorkspacesErrorAtom, null);

    // Trigger handler to perform the actual API call
    callbacks.onFetchRemoteWorkspaces();
  } else {
    console.error(
      "❌ No workspace handler callback available for fetching remote workspaces",
    );
  }
});

// Handler callbacks atom for workspace operations
export const workspaceHandlerCallbacksAtom = atom<{
  onFetchRemoteWorkspaces?: () => Promise<void>;
}>({});

/**
 * Combined workspaces atom - shows either remote workspaces OR default workspace
 * If remote workspaces exist, show only those
 * If no remote workspaces, show only the default workspace from env config
 */
export const availableWorkspacesAtom = atom((get) => {
  const remoteWorkspaces = get(remoteWorkspacesAtom);

  // If we have remote workspaces, use only those
  if (remoteWorkspaces.length > 0) {
    // Convert remote workspaces to local workspace format
    return remoteWorkspaces.map(
      (remote): Workspace => ({
        id: remote.id,
        name: remote.name,
        description: remote.description || "",
        type: "chat" as const, // Default type for remote workspaces
        icon: "💬",
        color: "#3b82f6",
        createdAt: remote.createdAt ? new Date(remote.createdAt) : new Date(),
        updatedAt: remote.updatedAt ? new Date(remote.updatedAt) : new Date(),
      }),
    );
  }

  // If no remote workspaces, show only the default workspace
  const defaultWorkspace: Workspace = {
    id: DEFAULT_WORKSPACE_ID,
    name: "Default Workspace",
    description: "Your default IVE workspace",
    type: "chat" as const,
    icon: "💬",
    color: "#3b82f6",
    createdAt: new Date(),
    updatedAt: new Date(),
  };
  return [defaultWorkspace];
});

// ===== WORKSPACE SELECTION =====

/**
 * Select workspace action atom
 * Sets the active workspace ID and triggers workspace initialization
 */
export const selectWorkspaceAtom = atom(
  null,
  (_get, set, workspaceId: string) => {
    console.log("🏢 Selecting workspace:", workspaceId);

    // Set the active workspace ID
    set(activeWorkspaceIdAtom, workspaceId);

    // The workspace initialization will be handled by the App component
    // when it detects the activeWorkspaceIdAtom change
    console.log("✅ Workspace selection completed:", workspaceId);
  },
);
